import React from 'react';

import { SxProps } from '@mui/material';

import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Theme,
  Typography,
} from '@sbv-clients/ui-shared';

export type CheckboxGroupOption<TValue> = {
  value: TValue;
  label: React.ReactNode;
  disabled?: boolean;
};

export type CheckboxGroupVariant = 'default' | 'card';

export type CheckboxGroupProps<TValue> = {
  name?: string;
  label?: React.ReactNode;
  value?: TValue[];
  options: CheckboxGroupOption<TValue>[];
  onChange?: (next: TValue[]) => void;

  row?: boolean;
  disabled?: boolean;

  error?: boolean;
  helperText?: React.ReactNode;

  variant?: CheckboxGroupVariant;
  sx?: SxProps<Theme>;
  listSx?: SxProps<Theme>;

  cardHeight?: number | string;
  maxCardHeight?: number | string;
};

export const CheckboxGroup = <TValue extends string | number>({
  name,
  label,
  value = [],
  options,
  onChange,
  row,
  disabled,
  error,
  helperText,
  variant = 'default',
  sx,
  listSx,
  cardHeight,
  maxCardHeight,
}: CheckboxGroupProps<TValue>) => {
  const isCard = variant === 'card';

  const toggle = (v: TValue) => {
    const exists = value.includes(v);
    const next = exists ? value.filter((x) => x !== v) : [...value, v];
    onChange?.(next);
  };

  return (
    <FormControl error={!!error} disabled={disabled} sx={sx}>
      <Box
        sx={
          isCard
            ? (t) => ({
                position: 'relative',
                border: `1px solid ${
                  error ? t.palette.error.main : t.palette.divider
                }`,
                borderRadius: 1,
                p: 1,
                bgcolor: 'background.paper',
              })
            : undefined
        }
      >
        {isCard && label ? (
          <Typography
            component="span"
            sx={(t) => ({
              position: 'absolute',
              top: -6,
              left: 12,
              px: 0.5,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.6px',
              color: 'text.secondary',
              backgroundColor: t.palette.background.paper,
              lineHeight: 1,
            })}
          >
            {label}
          </Typography>
        ) : null}

        <Box
          sx={{
            minHeight: 0,
            height: cardHeight,
            maxHeight: maxCardHeight,
            overflowY: cardHeight || maxCardHeight ? 'auto' : 'visible',
          }}
        >
          <FormGroup
            row={row}
            sx={{
              '& .MuiFormControlLabel-root': {
                m: 0,
                minHeight: 32,
                py: 0.25,
              },
              '& .MuiFormControlLabel-label': {
                fontSize: 13,
                lineHeight: '20px',
              },
              '& .MuiCheckbox-root': {
                p: 2,
              },
              ...listSx,
            }}
          >
            {options.map((opt) => {
              const checked = value.includes(opt.value);

              return (
                <FormControlLabel
                  key={String(opt.value)}
                  label={opt.label}
                  disabled={disabled || opt.disabled}
                  control={
                    <Checkbox
                      name={name}
                      checked={checked}
                      onChange={() => toggle(opt.value)}
                    />
                  }
                />
              );
            })}
          </FormGroup>
        </Box>
      </Box>

      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
};
